import { JSX, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { Box, TrashIcon } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { BaseOptionType, Select } from 'components/Select';
import { Input } from 'components/input/Input';
import { FormatPhoneNumber, RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import { calculateDensity } from 'utils/calculateDensity';
import { cn } from 'utils/shadcn';

interface CommodityItemCardProps {
  index: number;
  formMethods: UseFormReturn<NormalizedLoad>;
  onRemove: (index: number) => void;
}

const ANIMATIONS = {
  card: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
};

// Weight unit options
const weightUnitOptions = [
  { value: 'lbs', label: 'lbs' },
  { value: 'kg', label: 'kg' },
];

// Dimension unit options
const dimUnitOptions = [
  { value: 'inches', label: 'Inches' },
  { value: 'feet', label: 'Feet' },
];

// HazMat Group options
const hazmatGroupOptions = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Class I' },
  { value: 2, label: 'Class II' },
  { value: 3, label: 'Class III' },
];

// HazMat Class options
const hazmatClassOptions = [
  { value: 0, label: 'Class not specified' },
  { value: 1, label: 'Class 1 Explosive: division not specified' },
  { value: 11, label: 'Class 1.1 Explosive: mass' },
  { value: 12, label: 'Class 1.2 Explosive: projection' },
  { value: 13, label: 'Class 1.3 Explosive: fire and minor blast/projection' },
  { value: 14, label: 'Class 1.4 Explosive: minor' },
  { value: 15, label: 'Class 1.5 Explosive: insensitive mass' },
  { value: 16, label: 'Class 1.6 Explosive: insensitive minor' },
  { value: 2, label: 'Class 2 Gas: division not specified' },
  { value: 21, label: 'Class 2.1 Gas: flammable' },
  { value: 22, label: 'Class 2.2 Gas: nonflammable/nonpoisonous/oxygen' },
  { value: 23, label: 'Class 2.3 Gas: poison' },
  { value: 3, label: 'Class 3 Flammable Liquid and Combustible Liquid' },
  { value: 4, label: 'Class 4 Solid: division not specified' },
  { value: 41, label: 'Class 4.1 Flammable Solid' },
  { value: 42, label: 'Class 4.2 Spontaneously Combustible Solid' },
  { value: 43, label: 'Class 4.3 Dangerous When Wet' },
  { value: 5, label: 'Class 5 Oxidizer: division not specified' },
  { value: 51, label: 'Class 5.1 Oxidizer' },
  { value: 52, label: 'Class 5.2 Organic Peroxide' },
  { value: 6, label: 'Class 6 Poison, Toxic or Inhalation Hazard' },
  { value: 61, label: 'Class 6.1 Toxic and Infectious Substances' },
  { value: 7, label: 'Class 7 Radioactive Materials' },
  { value: 8, label: 'Class 8 Corrosive' },
  { value: 9, label: 'Class 9 Miscellaneous' },
  { value: 10, label: 'Class 1 Explosive: division not specified' },
];

// HazMat Prefix options
const hazmatPrefixOptions = [
  { value: 0, label: 'UN' },
  { value: 1, label: 'NA' },
  { value: 2, label: 'ID' },
];

export function CommodityItemCard({
  index,
  formMethods,
  onRemove,
}: CommodityItemCardProps): JSX.Element {
  const {
    watch,
    control,
    formState: { errors },
  } = formMethods;
  const watchedItem = watch(`commodities.${index}`);

  const isEmptyItem = !watchedItem?.description;
  const isHazmat = watchedItem?.hazardousMaterial || false;

  // Calculate density using utility function: weight_lb / (pallet_count × volume_per_pallet_cuft)
  const density = useMemo(() => {
    const weightAmount = watchedItem?.weightTotal || null;
    const weightUnit = watchedItem?.weightUnit || 'lbs';
    const length = watchedItem?.length || null;
    const width = watchedItem?.width || null;
    const height = watchedItem?.height || null;
    const dimensionUnit = watchedItem?.dimensionUnit || 'inches';
    const handlingUnitCount = watchedItem?.handlingQuantity || null;

    // Map string units to IDs for calculateDensity function
    // weightUnitId: 0 for kg, 1 for lbs
    const weightUnitId = weightUnit === 'kg' ? 0 : 1;
    // dimUnitId: 0 for inches, 1 for feet
    // The calculateDensity function expects 0 for inches, 1 for feet
    const dimUnitId = dimensionUnit === 'inches' ? 0 : 1;

    const calculatedDensity = calculateDensity(
      weightAmount,
      weightUnitId,
      length,
      width,
      height,
      dimUnitId,
      handlingUnitCount
    );

    if (calculatedDensity !== null && calculatedDensity !== undefined) {
      return calculatedDensity.toFixed(2);
    }
    return '0.00';
  }, [
    watchedItem?.weightTotal,
    watchedItem?.weightUnit,
    watchedItem?.length,
    watchedItem?.width,
    watchedItem?.height,
    watchedItem?.dimensionUnit,
    watchedItem?.handlingQuantity,
  ]);

  // Render the editing form for the commodity card
  const renderEditingForm = () => (
    <motion.div key='editing-form' className='overflow-hidden'>
      {/* Top header with item number and actions */}
      <Flex justify='between' align='center' className='mb-4'>
        <Flex align='center' gap='sm'>
          <Box className='h-6 w-6 text-neutral-500' />
          <Typography
            variant='caption'
            className='text-neutral-600 font-semibold'
          >
            Shipment {index + 1}
          </Typography>
        </Flex>

        {/* Remove button */}
        <div onClick={(e) => e.stopPropagation()}>
          <ActionButton
            onClick={() => onRemove(index)}
            className='hover:bg-error-300 text-error-600'
            icon={TrashIcon}
            aria-label='Remove item'
          />
        </div>
      </Flex>

      {/* Row 1: Commodity Description */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <div className='col-span-2'>
          <RHFTextInput
            name={`commodities.${index}.description`}
            label='Commodity Description'
            required={true}
          />
        </div>
      </Grid>

      {/* Row 2: Weight, Weight Units */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`commodities.${index}.weightTotal`}
          label='Weight'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={true}
        />
        <div>
          <Label name={`commodities.${index}.weightUnit`} required={false}>
            Weight Units
          </Label>
          <Controller
            name={`commodities.${index}.weightUnit`}
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8 text-neutral-500'
                placeholder='Choose'
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                onChange={(value) => field.onChange(value)}
                value={field.value || 'lbs'}
                options={weightUnitOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      {/* Row 3: Pallet Count, Piece Count */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`commodities.${index}.handlingQuantity`}
          label='Pallet Count'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
        <RHFTextInput
          name={`commodities.${index}.quantity`}
          label='Piece Count'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
      </Grid>

      {/* Row 4: Length, Width */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`commodities.${index}.length`}
          label='Length'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
        <RHFTextInput
          name={`commodities.${index}.width`}
          label='Width'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
      </Grid>

      {/* Row 5: Height, Dimension Unit */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`commodities.${index}.height`}
          label='Height'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
        <div>
          <Label name={`commodities.${index}.dimensionUnit`} required={false}>
            Dimension Unit
          </Label>
          <Controller
            name={`commodities.${index}.dimensionUnit`}
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8 text-neutral-500'
                placeholder='Choose'
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                onChange={(value) => field.onChange(value)}
                value={field.value || 'inches'}
                options={dimUnitOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      {/* Row 6: Density */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <div>
          <Label name='density' required={false}>
            Density
          </Label>
          <Input
            readOnly
            className='mt-1'
            value={`${density} lb/cu ft`}
            aria-label='Density'
          />
        </div>
      </Grid>

      {/* Row 7: Stackable checkbox */}
      {/* TODO: Don't have proper hazmat details to test this feature - will add hazmat checkbox and accordion in future */}
      <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
        <div className='flex items-center gap-4'>
          <Controller
            name={`commodities.${index}.stackable`}
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value ? true : false}
                onCheckedChange={field.onChange}
                label='Stackable'
              />
            )}
          />
          {/* Hazmat checkbox hidden for now - TODO: Don't have proper hazmat details to test this feature */}
        </div>
      </Grid>

      {/* HazMat fields hidden for now - TODO: Don't have proper hazmat details to test this feature */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && isHazmat && (
        <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
          <div>
            <Label
              name={`commodities.${index}.hazmatGroupId`}
              required={isHazmat}
            >
              {isHazmat ? '*HazMat Group' : 'HazMat Group'}
            </Label>
            <Controller
              name={`commodities.${index}.hazmatGroupId`}
              control={control}
              rules={{
                required: isHazmat ? 'HazMat Group is required' : false,
              }}
              render={({ field }) => (
                <Select
                  showSearch
                  className='h-8 text-neutral-500'
                  placeholder='Choose'
                  filterOption={(
                    input: string,
                    option: Undef<BaseOptionType>
                  ) =>
                    String(option?.label)
                      .toLocaleLowerCase()
                      .includes(input.toLocaleLowerCase())
                  }
                  onChange={(value) => field.onChange(Number(value))}
                  value={field.value !== undefined ? String(field.value) : ''}
                  options={hazmatGroupOptions.map((opt) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }))}
                />
              )}
            />
            {errors.commodities?.[index]?.hazmatGroupId && (
              <Typography variant='body-sm' className='text-error-500 mt-1'>
                {errors.commodities?.[index]?.hazmatGroupId?.message}
              </Typography>
            )}
          </div>
        </Grid>
      )}

      {/* Row 8: HazMat Class (conditional on HazMat) - Hidden for now */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && isHazmat && (
        <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
          <div>
            <Label
              name={`commodities.${index}.hazmatClassId`}
              required={isHazmat}
            >
              {isHazmat ? '*HazMat Class' : 'HazMat Class'}
            </Label>
            <Controller
              name={`commodities.${index}.hazmatClassId`}
              control={control}
              rules={{
                required: isHazmat ? 'HazMat Class is required' : false,
              }}
              render={({ field }) => (
                <Select
                  showSearch
                  className='h-8 text-neutral-500'
                  placeholder='Choose'
                  filterOption={(
                    input: string,
                    option: Undef<BaseOptionType>
                  ) =>
                    String(option?.label)
                      .toLocaleLowerCase()
                      .includes(input.toLocaleLowerCase())
                  }
                  onChange={(value) => field.onChange(Number(value))}
                  value={field.value !== undefined ? String(field.value) : ''}
                  options={hazmatClassOptions.map((opt) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }))}
                />
              )}
            />
            {errors.commodities?.[index]?.hazmatClassId && (
              <Typography variant='body-sm' className='text-error-500 mt-1'>
                {errors.commodities?.[index]?.hazmatClassId?.message}
              </Typography>
            )}
          </div>
        </Grid>
      )}

      {/* Row 9: Prefix and Hazmat Code (conditional on HazMat) - Hidden for now */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && isHazmat && (
        <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
          <div>
            <Label
              name={`commodities.${index}.hazmatPrefixId`}
              required={isHazmat}
            >
              {isHazmat ? '*Prefix' : 'Prefix'}
            </Label>
            <Controller
              name={`commodities.${index}.hazmatPrefixId`}
              control={control}
              rules={{ required: isHazmat ? 'Prefix is required' : false }}
              render={({ field }) => (
                <Select
                  showSearch
                  className='h-8 text-neutral-500'
                  placeholder='Choose'
                  filterOption={(
                    input: string,
                    option: Undef<BaseOptionType>
                  ) =>
                    String(option?.label)
                      .toLocaleLowerCase()
                      .includes(input.toLocaleLowerCase())
                  }
                  onChange={(value) => field.onChange(Number(value))}
                  value={field.value !== undefined ? String(field.value) : ''}
                  options={hazmatPrefixOptions.map((opt) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }))}
                />
              )}
            />
            {errors.commodities?.[index]?.hazmatPrefixId && (
              <Typography variant='body-sm' className='text-error-500 mt-1'>
                {errors.commodities?.[index]?.hazmatPrefixId?.message}
              </Typography>
            )}
          </div>
          <div>
            <Label name={`commodities.${index}.hazmatCode`} required={isHazmat}>
              {isHazmat ? '*Hazmat Code' : 'Hazmat Code'}
            </Label>
            <Controller
              name={`commodities.${index}.hazmatCode`}
              control={control}
              rules={{
                required: isHazmat ? 'Hazmat code is required' : false,
                pattern: {
                  value: /^\d{4}$/,
                  message: 'Hazmat code must be 4 digits',
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder='XXXX'
                  maxLength={4}
                  className={cn(
                    'h-8 text-neutral-500',
                    errors.commodities?.[index]?.hazmatCode
                      ? 'border-error-500'
                      : ''
                  )}
                />
              )}
            />
            {errors.commodities?.[index]?.hazmatCode && (
              <Typography variant='body-sm' className='text-error-500 mt-1'>
                {errors.commodities?.[index]?.hazmatCode?.message}
              </Typography>
            )}
          </div>
        </Grid>
      )}

      {/* Row 10: Chemical Name (conditional on HazMat) - Hidden for now */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && isHazmat && (
        <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
          <div className='col-span-2'>
            <RHFTextInput
              name={`commodities.${index}.hazmatChemicalName`}
              label={isHazmat ? '*Chemical Name' : 'Chemical Name'}
              required={isHazmat}
            />
          </div>
        </Grid>
      )}

      {/* Row 11: Emergency Contact Number (conditional on HazMat) - Hidden for now */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && isHazmat && (
        <Grid cols='2' gap='sm' className='mx-0 w-full mb-2'>
          <div className='col-span-2'>
            <Label
              name={`commodities.${index}.hazmatEmergencyContact`}
              required={isHazmat}
            >
              {isHazmat
                ? '*Emergency Contact Number'
                : 'Emergency Contact Number'}
            </Label>
            <Controller
              name={`commodities.${index}.hazmatEmergencyContact`}
              control={control}
              rules={{
                required: isHazmat
                  ? 'Emergency contact number is required'
                  : false,
                validate: (value) => {
                  if (!value && !isHazmat) return true;
                  if (!value && isHazmat)
                    return 'Emergency contact number is required';
                  if (!value) return true;
                  // Strip all non-digits and check length
                  const phoneDigits = value.replace(/[^\d]/g, '');
                  if (phoneDigits.length < 10) {
                    return 'Phone number must be at least 10 digits';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  onChange={(e) => {
                    const formatted = FormatPhoneNumber(e.target.value);
                    field.onChange(formatted);
                  }}
                  placeholder='(555) 555-5555'
                  type='tel'
                  className={cn(
                    'h-8 text-neutral-500',
                    errors.commodities?.[index]?.hazmatEmergencyContact
                      ? 'border-error-500'
                      : ''
                  )}
                />
              )}
            />
            {errors.commodities?.[index]?.hazmatEmergencyContact && (
              <Typography variant='body-sm' className='text-error-500 mt-1'>
                {errors.commodities?.[index]?.hazmatEmergencyContact?.message}
              </Typography>
            )}
          </div>
        </Grid>
      )}
    </motion.div>
  );

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className={cn(
        'overflow-hidden rounded-[4px] border bg-linear-to-br shadow-sm relative transition-all duration-200 p-2.5',
        isEmptyItem
          ? 'border-neutral-400 bg-linear-to-br from-neutral-50 to-neutral-100/50'
          : 'border-neutral-500 bg-linear-to-br from-neutral-50 to-info-50/50'
      )}
    >
      <AnimatePresence mode='wait'>{renderEditingForm()}</AnimatePresence>
    </motion.div>
  );
}
